import { ChangeDetectorRef, Component, Input, OnDestroy } from '@angular/core';
import { BaseDashboardFiltersComponent } from '../base-dashboard-filters.component';
import { GaValidateService } from '@app/shared/formly/ga-validate.service';
import { GaMessageService } from '@app/shared/message/ga-message.service';
import { TranslationService } from '@app/shared/translation/translation.service';
import { ReportDataService } from '@app/shared/report/report-data.service';
import { ReportFiltersUtil } from '@app/shared/report/report-filters-util.service';
import { DashboardService } from '../../dashboard.service';
import { GaDeferredService } from '@app/shared/services/ga-deferred.service';
import { AuthService } from '@app/shared/services/auth/auth.service';
import { DashboardDataService } from '@app/shared/report/dashboard-data.service';
import { CustomerDataService } from '../customer/customer-data.service';
import { Override } from '@app/shared/app-decorators';
import { GaFormlyService, IExtraFieldConfigurationObject } from '@app/shared/formly/ga-formly.service';
import { FormattingService } from '@app/shared/services/formatting.service';
import { ICodeValue, INamedObject } from '@type_definitions/datatypes';
import _ from 'underscore';
import { WebshopDataService } from '@app/main/webshop/webshop-data.service';
import { CompanyDataService } from '@app/shared/company/company-data.service';
import { CountryDataService } from '@app/shared/location/country-data.service';
import { OdDataService } from '../od/od-data.service';
import { ContactDataService } from '@app/shared/contact/contact-data.service';

export enum HitRateType {
  CUSTOMER = 'customer',
  COUNTRY = 'country'
}

const DEFAULT_MONTHS_RANGE:number = 12;

@Component({
  selector: 'hit-rate-dashboard-filters',
  templateUrl: '../base-dashboard-filters.component.html'
})
export class HitRateDashboardFiltersComponent extends BaseDashboardFiltersComponent implements OnDestroy {
  @Input() public hitRateType:HitRateType;

  private countryField:IExtraFieldConfigurationObject;
  private oemField:IExtraFieldConfigurationObject;
  private bpField:IExtraFieldConfigurationObject;
  private bpGroupField:IExtraFieldConfigurationObject;
  private customerField:IExtraFieldConfigurationObject;
  private lamField:IExtraFieldConfigurationObject;
  private lamContactField:IExtraFieldConfigurationObject;
  private lamCompanyField:IExtraFieldConfigurationObject;
  private lamGroupField:IExtraFieldConfigurationObject;
  private gbdmField:IExtraFieldConfigurationObject;
  private gbdmDeskField:IExtraFieldConfigurationObject;
  private armField:IExtraFieldConfigurationObject;
  private geoField:IExtraFieldConfigurationObject;
  private vnamField:IExtraFieldConfigurationObject;
  private pssField:IExtraFieldConfigurationObject;
  private bpDeskField:IExtraFieldConfigurationObject;
  private totalMonthsRangeField:IExtraFieldConfigurationObject;

  constructor(
    private formlyService:GaFormlyService,
    private formattingService:FormattingService,
    private dashboardDataService:DashboardDataService,
    private customerDataService:CustomerDataService,
    private webshopDataService:WebshopDataService,
    private companyDataService:CompanyDataService,
    private countryDataService:CountryDataService,
    private contactDataService:ContactDataService,
    private odDataService:OdDataService,
    validateService:GaValidateService,
    messageService:GaMessageService,
    translationService:TranslationService,
    reportDataService:ReportDataService,
    reportFiltersUtil:ReportFiltersUtil,
    dashboardService:DashboardService,
    deferredService:GaDeferredService,
    authService:AuthService,
    cdr:ChangeDetectorRef
  ) {
    super(validateService, messageService, translationService, reportDataService, reportFiltersUtil, dashboardService, deferredService, authService, cdr);
  }

  protected customValidatorFn = ():Promise<void | string[]> => {
    const processErrorMessage = (mandatoryFilters:string):string => {
      return `Please select at least one of the following filters: ${mandatoryFilters}.`;
    };

    switch (this.hitRateType) {
      case HitRateType.COUNTRY:
        if (!this.model.country && !this.model.bp) {
          return Promise.reject(processErrorMessage('Country or BP'));
        }
        break;

      case HitRateType.CUSTOMER:
        if (!this.model.customer) {
          return Promise.reject(processErrorMessage('Customer'));
        }
        break;
    }

    return Promise.resolve();
  };

  @Override(BaseDashboardFiltersComponent)
  public fillFields():void {
    const promises:Array<Promise<any>> = [];

    switch (this.hitRateType) {
      case HitRateType.COUNTRY:
        if (this.isACSLUser) {
          promises.push(
            this.formlyService.fillField(this.pssField, this.dashboardDataService.ODs())
              .then(() => {
                this.pssField.formControl.setValue(_.findWhere(this.pssField.templateOptions.options, { id: this.filters[ 'od' ] || this.currentUser.id }));
              })
          );

          promises.push(
            this.formlyService.fillField(this.bpDeskField, this.dashboardDataService.odTeams())
          );
        }
        break;

      case HitRateType.CUSTOMER:
        promises.push(
          this.formlyService.fillField(this.customerField, this.webshopDataService.webshopsWithReadOnlyAccess())
        );

        promises.push(
          this.formlyService.fillField(this.lamField, this.dashboardDataService.LAMs())
        );

        promises.push(
          this.formlyService.fillField(this.lamContactField, this.dashboardDataService.LAMContacts())
            .then((lamContacts:INamedObject[]) => {
              // preselect the current user
              this.lamContactField.formControl.setValue(_.findWhere(lamContacts, { id: this.filters[ 'lamContact' ] || this.currentUser.id }));
            })
        );

        promises.push(
          this.formlyService.fillField(this.lamCompanyField, this.dashboardDataService.LAMCompanies())
        );

        promises.push(
          this.formlyService.fillField(this.lamGroupField, this.customerDataService.LAMGroups())
        );

        promises.push(
          this.formlyService.fillField(this.gbdmField, this.dashboardDataService.gbdms())
        );

        promises.push(
          this.formlyService.fillField(this.gbdmDeskField, this.dashboardDataService.departments(34))
        );

        promises.push(
          this.formlyService.fillField(this.oemField, this.companyDataService.getVendorCompanies())
        );

        promises.push(
          this.formlyService.fillField(this.geoField, this.countryDataService.listContinents())
        );

        promises.push(
          this.formlyService.fillField(this.vnamField, this.contactDataService.NAMs('VNAM'))
        );

        // check if current user is acsl user
        if (this.isACSLUser) {
          promises.push(
            this.formlyService.fillField(this.armField, this.dashboardDataService.gbdms())
          );
        }
        break;
    }

    promises.push(
      this.formlyService.fillField(this.countryField, this.customerDataService.countries())
    );

    promises.push(
      this.formlyService.fillField(this.bpField, this.dashboardDataService.BPs())
    );

    promises.push(
      this.formlyService.fillField(this.bpGroupField, this.odDataService.BPGroups())
    );

    promises.push(
      this.formlyService.fillField(this.totalMonthsRangeField, this.reportDataService.loadListFilterOptions(175, '101'))
        .then((totalMonths:ICodeValue[]) => {
          this.totalMonthsRangeField.formControl.setValue(_.findWhere(totalMonths, { id: String(DEFAULT_MONTHS_RANGE) }));
        })
    );

    Promise.all(promises)
      .then(this.setQueryFilters);
  }

  @Override(BaseDashboardFiltersComponent)
  public createFields():IExtraFieldConfigurationObject[] {
    return [
      this.countryField = this.formlyService.aSelect('country', 'Country')
        .withWrapperClass(this.wrapperClass)
        .withFormat(this.formattingService.formatCountry)
        .withExtraOptions({
          templateOptions: {
            localSearch: true
          }
        })
        .build(),

      this.bpField = this.formlyService.aSearch('bp', 'BP')
        .withWrapperClass(this.wrapperClass)
        .withFormat(this.formattingService.formatBPNicknameCountry)
        .withExtraOptions({
          templateOptions: {
            localSearch: true
          }
        })
        .build(),

      this.bpGroupField = this.formlyService.aSearch('bpGroup', 'BP group')
        .withWrapperClass(this.wrapperClass)
        .withFormat(this.formattingService.formatName)
        .withExtraOptions({
          templateOptions: {
            localSearch: true
          }
        })
        .build(),

      this.customerField = this.formlyService.aSearch('customer', 'Customer')
        .withWrapperClass(this.wrapperClass)
        .withFormat(this.formattingService.formatCompany)
        .withExtraOptions({
          templateOptions: {
            localSearch: true
          }
        })
        .withHideExpression(() => this.hitRateType !== HitRateType.CUSTOMER)
        .build(),

      this.lamField = this.formlyService.aSearch('lam', 'LAM')
        .withWrapperClass(this.wrapperClass)
        .withFormat(this.formattingService.formatName)
        .withExtraOptions({
          templateOptions: {
            localSearch: true
          }
        })
        .withHideExpression(() => this.hitRateType !== HitRateType.CUSTOMER)
        .build(),

      this.lamContactField = this.formlyService.aSearch('lamContact', 'LAM Contact')
        .withWrapperClass(this.wrapperClass)
        .withFormat(this.formattingService.formatName)
        .withExtraOptions({
          templateOptions: {
            localSearch: true
          }
        })
        .withHideExpression(() => this.hitRateType !== HitRateType.CUSTOMER)
        .build(),

      this.lamCompanyField = this.formlyService.aSearch('lamCompany', 'LAM Company')
        .withWrapperClass(this.wrapperClass)
        .withFormat(this.formattingService.formatName)
        .withExtraOptions({
          templateOptions: {
            localSearch: true
          }
        })
        .withHideExpression(() => this.hitRateType !== HitRateType.CUSTOMER)
        .build(),

      this.lamGroupField = this.formlyService.aSearch('lamGroup', 'LAM Group')
        .withWrapperClass(this.wrapperClass)
        .withFormat(this.formattingService.formatName)
        .withExtraOptions({
          templateOptions: {
            localSearch: true
          }
        })
        .withHideExpression(() => this.hitRateType !== HitRateType.CUSTOMER)
        .build(),

      this.oemField = this.formlyService.aSelect('oem', 'OEM')
        .withWrapperClass(this.wrapperClass)
        .withFormat(this.formattingService.formatCompany)
        .withExtraOptions({
          templateOptions: {
            localSearch: true
          }
        })
        .withHideExpression(() => this.hitRateType !== HitRateType.CUSTOMER)
        .build(),

      this.gbdmField = this.formlyService.aSelect('gbdm', 'GBDM')
        .withWrapperClass(this.wrapperClass)
        .withFormat(this.formattingService.formatContact)
        .withExtraOptions({
          templateOptions: {
            localSearch: true
          }
        })
        .withHideExpression(() => this.hitRateType !== HitRateType.CUSTOMER)
        .build(),

      this.gbdmDeskField = this.formlyService.aSelect('gbdmDesk', 'GBDM Desk')
        .withWrapperClass(this.wrapperClass)
        .withFormat(this.formattingService.formatDepartment)
        .withHideExpression(() => this.hitRateType !== HitRateType.CUSTOMER)
        .build(),

      this.geoField = this.formlyService.aSearch('geography', 'Geography')
        .withWrapperClass(this.wrapperClass)
        .withFormat(this.formattingService.formatName)
        .withExtraOptions({
          templateOptions: {
            localSearch: true
          }
        })
        .withHideExpression(() => this.hitRateType !== HitRateType.CUSTOMER)
        .build(),

      this.vnamField = this.formlyService.aSearch('vnam', 'VNAM')
        .withWrapperClass(this.wrapperClass)
        .withFormat(this.formattingService.formatContact)
        .withExtraOptions({
          templateOptions: {
            localSearch: true
          }
        })
        .withHideExpression(() => this.hitRateType !== HitRateType.CUSTOMER)
        .build(),

      this.armField = this.formlyService.aSelect('arm', 'ARM')
        .withWrapperClass(this.wrapperClass)
        .withFormat(this.formattingService.formatContact)
        .withHideExpression(() => !(this.isACSLUser && this.hitRateType === HitRateType.CUSTOMER))
        .build(),

      this.pssField = this.formlyService.aSelect('pss', 'PSS')
        .withWrapperClass(this.wrapperClass)
        .withFormat(this.formattingService.formatContact)
        .withHideExpression(() => !(this.isACSLUser && this.hitRateType === HitRateType.COUNTRY))
        .build(),


      this.bpDeskField = this.formlyService.aMultiSelect('bpDesk', 'BP Desk')
        .withWrapperClass(this.wrapperClass)
        .withValueProp('id')
        .withLabelProp('description')
        .withExtraOptions({
          templateOptions: {
            localSearch: true
          }
        })
        .withHideExpression(() => !(this.isACSLUser && this.hitRateType === HitRateType.COUNTRY))
        .build(),

      this.totalMonthsRangeField = this.formlyService.aSelect('totalMonthsRange', 'Total months range')
        .withWrapperClass(this.wrapperClass)
        .withFormat((option:ICodeValue):string => option.value)
        .build()
    ];
  }

  public ngOnDestroy():void {
    this.dashboardDataService.clear();
    this.customerDataService.clear();
  }
}